'use client';

import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useAppDispatch, store } from '@/store/store';
import workspaceApi from '@/store/api/workspaceApi';
import { addColumn } from '@/store/slices/workspaceColumnsSlice';
import {
  phoneBulkStarted,
  phoneBulkTick,
  phoneBulkCleared,
  type WorkspacePhoneState,
} from '@/store/slices/workspacePhoneSlice';
import type { WorkspaceRow } from '@/lib/types/workspace.types';

const BULK_ROWS_DEBOUNCE_MS = 2500;

export interface BulkPhoneWorkflowEvent {
  workflowId?: unknown;
  event?: unknown;
  category?: unknown;
  bulkBatchId?: unknown;
  /** Present on the row's own event — see resolveExternalBulkBatch. */
  input?: { docId?: unknown; columnName?: unknown };
}

export function useWorkspaceBulkPhoneEvents() {
  const dispatch = useAppDispatch();
  const bulkRowsTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const unknownBulkBatches = useRef(new Map<string, 'resolving' | 'unresolvable'>());

  useEffect(() => {
    const timers = bulkRowsTimers.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  const scheduleBulkRowsRefetch = useCallback(
    (userUploadId: string, bulkBatchId: string) => {
      const timers = bulkRowsTimers.current;
      const prev = timers.get(bulkBatchId);
      if (prev) clearTimeout(prev);
      timers.set(
        bulkBatchId,
        setTimeout(() => {
          timers.delete(bulkBatchId);
          dispatch(workspaceApi.util.invalidateTags([{ type: 'WorkspaceRows', id: userUploadId }]));
          dispatch(phoneBulkCleared(bulkBatchId));
        }, BULK_ROWS_DEBOUNCE_MS)
      );
    },
    [dispatch]
  );

  /**
   * Adopts a bulk phone batch this browser tab didn't start (another tab, a
   * page reload mid-run, a direct API call) — toast + column + a debounced
   * rows refresh, the same outcome `phoneBulkStarted` gives a batch this tab
   * DID start.
   *
   * Resolved straight from the event's own `input.docId`/`input.columnName`
   * rather than a follow-up GET: this used to call `workflowApi.getWorkflow`
   * (`/api/workflows/:id`, the EMAIL waterfall's endpoint — copy-paste from
   * useWorkspaceBulkEvents.ts) with a *phone* workflow id, which 404s every
   * time against the Workflow-Service's email-only `workflows` collection.
   * The fix isn't "call the phone equivalent instead" — `phoneWorkflowApi`'s
   * `PhoneWorkflowSnapshot` doesn't carry `docId`/`columnName` at all (see
   * phoneWorkflowApi.ts), so no snapshot fetch here could ever resolve one.
   * The event already carries both fields directly (confirmed against a live
   * `workflow.finished` payload), so there's nothing to fetch.
   */
  const resolveExternalBulkBatch = useCallback(
    (bulkBatchId: string, input: BulkPhoneWorkflowEvent['input']) => {
      const seen = unknownBulkBatches.current;
      if (seen.has(bulkBatchId)) return;

      const docId = typeof input?.docId === 'string' ? input.docId : undefined;
      const columnName = typeof input?.columnName === 'string' ? input.columnName : undefined;
      const row = docId
        ? (store.getState() as { workspaceData: { byId: Record<string, WorkspaceRow> } }).workspaceData.byId[docId]
        : undefined;

      if (!columnName || !row) {
        // Not on this sheet's loaded page yet (or the event arrived before
        // `input` was populated) — leave it; opening the sheet later picks
        // values up via rows. Not marked 'resolving' first (unlike the old
        // async version) since there's no in-flight request to guard against
        // re-entering — this whole path is synchronous.
        seen.set(bulkBatchId, 'unresolvable');
        return;
      }

      const { userupload_id: userUploadId } = row;
      seen.set(bulkBatchId, 'resolving');
      dispatch(phoneBulkStarted({ bulkBatchId, userUploadId, columnName }));
      dispatch(addColumn({ key: columnName, name: columnName, type: 'phone' }));
      toast.success(`Phone enrichment started for "${columnName}" — values appear as rows are processed`);
      scheduleBulkRowsRefetch(userUploadId, bulkBatchId);
    },
    [dispatch, scheduleBulkRowsRefetch]
  );

  return useCallback(
    (p: BulkPhoneWorkflowEvent): boolean => {
      if (p.category !== 'phone' || typeof p.bulkBatchId !== 'string' || !p.bulkBatchId) {
        return false;
      }
      if (p.event === 'workflow.finished') dispatch(phoneBulkTick(p.bulkBatchId));
      const bulkRun = (store.getState() as { workspacePhone: WorkspacePhoneState })
        .workspacePhone?.bulkRunsByBatchId?.[p.bulkBatchId];
      if (bulkRun && bulkRun.userUploadId) {
        if (p.event === 'workflow.finished') scheduleBulkRowsRefetch(bulkRun.userUploadId, p.bulkBatchId);
      } else {
        resolveExternalBulkBatch(p.bulkBatchId, p.input);
      }
      return true;
    },
    [dispatch, resolveExternalBulkBatch, scheduleBulkRowsRefetch]
  );
}
